import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {fetchParticipantList, fetchStaffList, fetchStorageDetails} from 'api';
import {createAddress, getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as NoResultsIcon} from 'assets/svg/icon-no-results.svg';
import {ReactComponent as WarningIcon} from 'assets/svg/warning.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    createParticipantFx,
    loadParticipantsDataFx,
    updateParticipantFx,
} from 'models/profile/common';
import {boundUserWithParticipantFx} from 'models/profile/staff/table';
import {deactivateStorageFx} from 'models/profile/storages/table';
import {ParticipantRegistrationRequest, ParticipantUpdateRequest} from 'types';
import {Participant} from 'types/participants';
import {StaffAccessors as Accessors, StaffListFormattedRow, StaffListRawRow} from 'types/profile';
import {formatAddressInfoToRequest} from 'utils/address';
import {handleNil} from 'utils/handleNil';
import {mapStorageTypesToI18nOptions} from 'utils/select';

import {Wrapper} from '../../styled';

import {StorageStatusTag} from '../styled';

import {ConfirmBlockModal} from './ConfirmBlockModal';
import {
    ActionCell,
    AddressField,
    CancelConfirmButton,
    ConfirmDeleteButton,
    ConfirmRemoveFooter,
    ConfirmRemoveHeader,
    ConfirmRemoveHeaderIcon,
    ConfirmRemoveModalContent,
    ConfirmRemoveTitle,
    Container,
    EmptyEmployeesDescription,
    EmptyEmployeesState,
    EmptyEmployeesTitle,
    EmployeesHeader,
    EmployeesTableWrapper,
    EmployeesTitle,
    Field,
    FieldsGrid,
    Header,
    HeaderActions,
    InlineButton,
    ModalFooter,
    ModalCloseButton,
    RemoveEmployeeButton,
    RemoveConfirmationModal,
    Title,
    WarningMessage,
} from './styled';

const additionalStyles = `
    text-align: left;
    max-width: 195px;
    border-radius: 8px;
    opacity: 1 !important;
`;

type Mode = 'create' | 'details';

type Props = {
    mode: Mode;
};

type Params = {
    clientId?: string;
};

type FormField = {
    name: keyof ParticipantRegistrationRequest;
    options?: SelectOption<string | number>[];
};

type EmployeeOption = SelectOption<number> & {value: number};

const storageTypeOptions = (prefix: string) =>
    mapStorageTypesToI18nOptions(
        new Array(12).fill(0).map((_, i) => ++i),
        prefix,
    );

const isEmployeeOption = (option: SelectOption<number>): option is EmployeeOption =>
    option.value !== null;

const getFullName = (employee: StaffListRawRow) =>
    `${employee.firstName || ''} ${employee.middleName || ''} ${employee.lastName || ''}`.trim();

const mapParticipantToForm = (participant: Participant): ParticipantRegistrationRequest =>
    ({
        storageName: participant.storageName || '',
        storageGln: participant.storageGln || '',
        storageTypeId: participant.storageType?.id,
        storageAddress: participant.address?.address || '',
    } as ParticipantRegistrationRequest);

const defaultFormValues = {
    storageName: '',
    storageGln: '',
    storageAddress: '',
} as ParticipantRegistrationRequest;

const normalizeParticipantsResponse = (response: unknown): Participant[] => {
    if (Array.isArray(response)) return response as Participant[];

    const responseData = (response as {data?: unknown})?.data;
    if (Array.isArray(responseData)) return responseData as Participant[];

    return [];
};

const findParticipantInResponse = (response: unknown, currentParticipant: Participant | null) => {
    const participants = normalizeParticipantsResponse(response);

    return (
        participants.find((item) => item.id === currentParticipant?.id) ??
        participants.find((item) => item.storageName === currentParticipant?.storageName) ??
        null
    );
};

const patchParticipantFromForm = (
    participant: Participant,
    data: ParticipantRegistrationRequest,
): Participant => ({
    ...participant,
    storageName: data.storageName || '',
    storageGln: data.storageGln || '',
    storageType: {
        ...participant.storageType,
        id: data.storageTypeId,
    },
});

function convertEmployees(
    data: StaffListRawRow[] | undefined,
    onRemove?: (employee: StaffListRawRow) => void,
): StaffListFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [Accessors.id]: handleNil(elem.id),
        [Accessors.name]: handleNil(getFullName(elem)),
        [Accessors.email]: handleNil(elem.email),
        [Accessors.phone]: handleNil(elem.phone),
        [Accessors.actions]: onRemove ? (
            <ActionCell>
                <RemoveEmployeeButton type="button" onClick={() => onRemove(elem)}>
                    ×
                </RemoveEmployeeButton>
            </ActionCell>
        ) : null,
    }));
}

export const StorageDetails = ({mode}: Props) => {
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const {clientId} = useParams<Params>();
    const permissions = useStore(permissions$);
    const deactivatePending = useStore(deactivateStorageFx.pending);
    const mountedRef = useRef(true);
    const [isEditing, setIsEditing] = useState(mode === 'create');
    const [participant, setParticipant] = useState<Participant | null>(null);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [employees, setEmployees] = useState<StaffListRawRow[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<SelectOption<number>[]>([]);
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [
        employeeForRemoveConfirmation,
        setEmployeeForRemoveConfirmation,
    ] = useState<StaffListRawRow | null>(null);
    const [isLoadingParticipant, setIsLoadingParticipant] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [formDefaultValues, setFormDefaultValues] = useState<ParticipantRegistrationRequest>(
        defaultFormValues,
    );
    const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);

    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language, t],
    );

    const translateForm = useCallback(
        (key: string) => t(`pages.profile.participant.fields.${key}`),
        [i18n.language, t],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                storageName: yup.string().required(translateValidates('required')),
                storageGln: yup.string().notRequired(),
                storageTypeId: yup
                    .number()
                    .typeError(translateValidates('required'))
                    .required(translateValidates('required')),
                storageAddress: yup.string().required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors, isDirty},
        handleSubmit,
        reset,
        setError,
    } = useForm<ParticipantRegistrationRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: formDefaultValues,
    });

    const setFormSnapshot = useCallback(
        (values: ParticipantRegistrationRequest) => {
            setFormDefaultValues(values);
            reset(values);
        },
        [reset],
    );

    const formFields = useMemo<FormField[]>(
        () => [
            {name: 'storageName'},
            {name: 'storageGln'},
            {
                name: 'storageTypeId',
                options: storageTypeOptions('dictionaries.storageType'),
            },
            {name: 'storageAddress'},
        ],
        [i18n.language],
    );

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        [],
    );

    const loadStorageData = useCallback(async () => {
        let shouldSkipLoadingReset = false;
        if (mountedRef.current) {
            setIsLoadingParticipant(true);
        }

        try {
            if (mode === 'details') {
                if (!clientId) {
                    shouldSkipLoadingReset = true;
                    history.replace(ROUTE.STORAGES);
                    return;
                }

                const storageDetails = await fetchStorageDetails(clientId);

                if (!mountedRef.current) return;

                setParticipant(storageDetails);
                setFormSnapshot(mapParticipantToForm(storageDetails));
                return;
            }

            const response = await fetchParticipantList();

            if (!mountedRef.current) return;

            setParticipants(response);
        } catch (error) {
            if (!mountedRef.current) return;

            console.error(error);
            toast.error(error.message ?? i18n.t('errors.occurred'));
        } finally {
            if (mountedRef.current && !shouldSkipLoadingReset) {
                setIsLoadingParticipant(false);
            }
        }
    }, [clientId, history, i18n, mode, setFormSnapshot]);

    useEffect(() => {
        loadStorageData();
    }, [loadStorageData]);

    useEffect(() => {
        let canceled = false;

        if (assignModalVisible) {
            fetchStaffList()
                .then((data) => {
                    if (!canceled) {
                        setEmployees(data);
                    }
                })
                .catch((error) => {
                    if (canceled) return;

                    console.error(error);
                    toast.error(i18n.t('errors.occurred'));
                });
        }

        return () => {
            canceled = true;
        };
    }, [assignModalVisible, i18n]);

    const assignedEmployees = useMemo(() => {
        if (mode === 'details') return participant?.linkedUsers ?? [];

        return selectedEmployees.filter(isEmployeeOption).map((option) => {
            const employee = employees.find((item) => item.id === option.value);
            return {
                id: option.value,
                firstName: String(option.label),
                middleName: '',
                lastName: '',
                email: employee?.email || '',
                phone: employee?.phone || '',
                clientId: 0,
                username: '',
                emailVerified: false,
                phoneVerified: false,
            };
        });
    }, [employees, mode, participant?.linkedUsers, selectedEmployees]);

    const availableEmployeeOptions = useMemo<SelectOption<number>[]>(() => {
        const assignedIds = new Set(assignedEmployees.map((item) => item.id));
        return (
            employees
                // .filter((item) => !item.clientId || item.clientId === participant?.id)
                .filter((item) => !assignedIds.has(item.id))
                .map((item) => ({
                    value: item.id,
                    label: getFullName(item),
                }))
        );
    }, [assignedEmployees, employees, participant?.id]);

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang = 'en';
            const values = (await getAddressesSuggestList({address: partialAddress, lang})).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const canCreateStorage =
        permissions.includes(permissions.organizationParticipantOwnRegister) ||
        permissions.includes(permissions.participantAllRegister);
    const canEditStorage = permissions.includes(permissions.participantOwnEdit);
    const canManageEmployees =
        permissions.includes(permissions.userOwnEdit) ||
        permissions.includes(permissions.userAllEdit);
    const canDeactivateStorage =
        permissions.includes(permissions.userOwnEdit) ||
        permissions.includes(permissions.userAllEdit);
    const isDefaultStorage = participant?.isDefault;
    const storageHasMarkingCodes = participant?.hasMarkingCodes;

    const getAddressId = async (address?: string) => {
        if (!address) return;
        const lang = 'en';
        return getAddressInfoFromString({address, lang})
            .then(formatAddressInfoToRequest)
            .then(createAddress);
    };

    const onAssignEmployees = async () => {
        if (!canManageEmployees) return;

        if (mode === 'create') {
            setAssignModalVisible(false);
            return;
        }

        if (!participant?.id || selectedEmployees.length === 0) return;
        const employeeOptions = selectedEmployees.filter(isEmployeeOption);

        try {
            const responses = await Promise.all(
                employeeOptions.map((employee) =>
                    boundUserWithParticipantFx({
                        clientId: participant.id,
                        userId: employee.value,
                    }),
                ),
            );
            const updatedParticipants = normalizeParticipantsResponse(
                responses[responses.length - 1],
            );
            const updatedParticipant = findParticipantInResponse(updatedParticipants, participant);

            if (updatedParticipant) {
                setParticipant(updatedParticipant);
                setFormSnapshot(mapParticipantToForm(updatedParticipant));
            }
            setSelectedEmployees([]);
            setAssignModalVisible(false);
        } catch (error) {
            console.error(error);
            toast.error(i18n.t('errors.occurred'));
        }
    };

    const onConfirmRemoveEmployee = async () => {
        if (!canManageEmployees) return;

        const employee = employeeForRemoveConfirmation;
        if (!employee) return;

        if (mode === 'create') {
            setSelectedEmployees((items) => items.filter((item) => item.value !== employee.id));
            setEmployeeForRemoveConfirmation(null);
            return;
        }

        try {
            const removeEmployeeResponse = await boundUserWithParticipantFx({
                clientId: null,
                userId: employee.id,
            });
            const updatedParticipants = normalizeParticipantsResponse(removeEmployeeResponse);
            const updatedParticipant = findParticipantInResponse(updatedParticipants, participant);

            if (updatedParticipant) {
                setParticipant(updatedParticipant);
                setFormSnapshot(mapParticipantToForm(updatedParticipant));
            }
            setEmployeeForRemoveConfirmation(null);
        } catch (error) {
            console.error(error);
            // toast.error(i18n.t('errors.occurred'));
        }
    };

    const onSubmit = async (data: ParticipantRegistrationRequest) => {
        try {
            if (mode === 'create') {
                if (!canCreateStorage) return;

                const userIds = selectedEmployees
                    .filter(isEmployeeOption)
                    .map((employee) => employee.value);

                if (userIds.length === 0) {
                    toast.error(t('pages.profile.storages.validation.requiredEmployee'));
                    return;
                }

                const storageNameValue = data.storageName.trim().toLowerCase();
                const isDuplicate = participants.some(
                    (item) => item.storageName?.trim().toLowerCase() === storageNameValue,
                );

                if (isDuplicate) {
                    setError('storageName', {
                        type: 'manual',
                        message: t('pages.profile.storages.validation.duplicateName'),
                    });
                    return;
                }

                setIsSubmitLoading(true);
                const storageAddressId = await getAddressId(data.storageAddress);
                if (!storageAddressId) return;

                await createParticipantFx({
                    ...data,
                    storageAddressId,
                    userIds,
                } as ParticipantRegistrationRequest);
                await loadParticipantsDataFx();
                history.push(ROUTE.STORAGES);
                return;
            }

            if (!participant?.id) return;
            if (!canEditStorage) return;

            if (!participant.linkedUsers?.length) {
                toast.error(t('pages.profile.storages.validation.requiredEmployee'));
                return;
            }

            setIsSubmitLoading(true);
            const payload: ParticipantUpdateRequest & {id: string | number} = {
                gln: {value: data.storageGln || '', reset: false},
                storageTypeId: {value: data.storageTypeId, reset: false},
                storageName: {value: data.storageName || '', reset: false},
                id: participant.id,
            };

            const updateParticipantResponse = await updateParticipantFx(payload);
            const updatedParticipants = normalizeParticipantsResponse(updateParticipantResponse);
            const updatedParticipant = findParticipantInResponse(updatedParticipants, participant);
            const nextParticipant =
                updatedParticipant ?? patchParticipantFromForm(participant, data);

            setParticipants(updatedParticipants);
            setIsEditing(false);
            setParticipant(nextParticipant);
            setFormSnapshot(mapParticipantToForm(nextParticipant));
            history.replace(`${ROUTE.STORAGES}/${nextParticipant.id}`);
        } catch (error) {
            console.error(error);
            const responseData = (error as any).fullErrorsList;
            if (!Array.isArray(responseData)) {
                toast.error(i18n.t('errors.occurred'));
                return;
            }

            responseData.forEach(({field, message}) => setError(field, {type: 'manual', message}));
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const controlRender = (
        controlEvents: ControllerRenderProps<ParticipantRegistrationRequest>,
        field: FormField,
    ) => {
        const disabled =
            !isEditing ||
            (mode === 'details' && ['storageName', 'storageAddress'].includes(field.name));

        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled,
        };

        if (field.name === 'storageTypeId') {
            return (
                <Select
                    {...baseProps}
                    value={field.options?.find((o) => o.value === controlEvents.value) ?? null}
                    options={field.options}
                    onChange={(option: SelectOption<number>) =>
                        controlEvents.onChange(option.value)
                    }
                />
            );
        }

        if (field.name === 'storageAddress') {
            if (!isEditing) {
                return <Input value={controlEvents.value || ''} readOnly />;
            }

            return (
                <AsyncSelect
                    {...baseProps}
                    name={controlEvents.name}
                    loadOptions={addressOptions}
                    isClearable
                    onChange={(v?: {value: string; label: string}) => {
                        controlEvents.onChange(v?.value || '');
                    }}
                    value={
                        controlEvents.value
                            ? {value: controlEvents.value, label: controlEvents.value}
                            : null
                    }
                />
            );
        }

        return <Input {...baseProps} {...controlEvents} readOnly={!isEditing} />;
    };

    const columns = useMemo(
        () =>
            mapTableColumns([
                {
                    id: Accessors.name,
                    header: t('pages.profile.staff.name'),
                },
                {
                    id: Accessors.email,
                    header: t('pages.profile.staff.email'),
                },
                {
                    id: Accessors.phone,
                    header: t('pages.profile.staff.phone'),
                },
                ...(canManageEmployees
                    ? [
                          {
                              id: Accessors.actions,
                              header: t('pages.profile.staff.actions'),
                              width: 40,
                          },
                      ]
                    : []),
            ]),
        [t],
    );

    const mappedEmployees = useMemo(
        () =>
            convertEmployees(
                assignedEmployees,
                canManageEmployees ? setEmployeeForRemoveConfirmation : undefined,
            ),
        [assignedEmployees, canManageEmployees],
    );

    const pageTitle =
        mode === 'create'
            ? t('pages.profile.storages.createTitle')
            : t('pages.profile.storages.detailsTitle');
    const hasAssignedEmployees = assignedEmployees.length > 0;

    const emptyEmployeesState = (
        <EmptyEmployeesState>
            <Icon source={NoResultsIcon} width={80} height={80} />
            <EmptyEmployeesTitle>
                {t('pages.profile.storages.emptyEmployees.title')}
            </EmptyEmployeesTitle>
            <EmptyEmployeesDescription>
                {t('pages.profile.storages.emptyEmployees.description')}
            </EmptyEmployeesDescription>
            {canManageEmployees && (
                <InlineButton type="button" size="sm" onClick={() => setAssignModalVisible(true)}>
                    {t('pages.profile.storages.assignEmployees')} +
                </InlineButton>
            )}
        </EmptyEmployeesState>
    );

    const onCancel = () => {
        if (mode === 'details' && isEditing) {
            setIsEditing(false);
            reset(formDefaultValues);
            return;
        }

        history.push(ROUTE.STORAGES);
    };

    const onEditDetails = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        reset(formDefaultValues);
        setIsEditing(true);
    };

    const onDeactivateStorage = async (reason: string) => {
        if (!participant?.id) return;

        const payload = {
            clientId: participant.id,
            reason,
        };

        try {
            await deactivateStorageFx(payload);

            setIsBlockModalVisible(false);
            history.push(ROUTE.STORAGES);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <BackTo to={ROUTE.STORAGES}>{t('pages.profile.storages.back')}</BackTo>
                <Container status={isLoadingParticipant ? 'loading' : 'default'}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Header>
                            <Title>{pageTitle}</Title>
                            {mode === 'details' && participant && (
                                <StorageStatusTag isActive={participant.status === 'active'}>
                                    {participant.status === 'deactivated'
                                        ? t('dictionaries.profile.staff.status.BLOCKED')
                                        : t('dictionaries.profile.staff.status.ACTIVE')}
                                </StorageStatusTag>
                            )}
                            <HeaderActions>
                                {mode === 'details' && isEditing && (
                                    <InlineButton
                                        type="button"
                                        size="sm"
                                        variant={ButtonVariant.Outline}
                                        isLoading={isSubmitLoading}
                                        onClick={onCancel}
                                    >
                                        {t('pages.profile.staff.create.cancel')}
                                    </InlineButton>
                                )}
                                {mode === 'details' && !isEditing ? (
                                    canEditStorage && (
                                        <>
                                            {storageHasMarkingCodes && (
                                                <Tooltip
                                                    id={`blockTooltip-${participant?.id ?? ''}`}
                                                    place={'left'}
                                                    backgroundColor="white"
                                                    textColor="#111111"
                                                    additionalStyles={
                                                        additionalStyles +
                                                        'box-shadow: 0px 3px 6px rgba(116, 116, 116, 0.3);'
                                                    }
                                                />
                                            )}
                                            {canDeactivateStorage &&
                                                !isDefaultStorage &&
                                                participant?.status === 'active' && (
                                                    <InlineButton
                                                        data-tip={t(
                                                            'pages.profile.staff.blockStorageTooltip',
                                                        )}
                                                        data-for={`blockTooltip-${participant?.id ??
                                                            ''}`}
                                                        disabled={storageHasMarkingCodes}
                                                        type="button"
                                                        size="sm"
                                                        variant={ButtonVariant.Cancel}
                                                        onClick={() => setIsBlockModalVisible(true)}
                                                    >
                                                        {t('pages.profile.storages.blockStorage')}
                                                    </InlineButton>
                                                )}
                                            <InlineButton
                                                type="button"
                                                size="sm"
                                                variant={ButtonVariant.Secondary}
                                                onClick={onEditDetails}
                                            >
                                                {t('pages.profile.storages.editDetails')}
                                            </InlineButton>
                                        </>
                                    )
                                ) : (
                                    <InlineButton
                                        type="submit"
                                        size="sm"
                                        disabled={
                                            (mode === 'details' && !isDirty) ||
                                            (mode === 'details' && !canEditStorage) ||
                                            (mode === 'create' && !canCreateStorage)
                                        }
                                        isLoading={isSubmitLoading}
                                    >
                                        {mode === 'create'
                                            ? t('pages.profile.storages.createAction')
                                            : t('pages.profile.storages.saveChanges')}
                                    </InlineButton>
                                )}
                            </HeaderActions>
                        </Header>
                        <FieldsGrid>
                            {formFields.map((field) => {
                                const content = (
                                    <Field
                                        key={field.name}
                                        text={(errors?.[field.name] as FieldError)?.message}
                                        isError={!!errors?.[field.name]}
                                    >
                                        <span>{translateForm(field.name)}</span>
                                        <Controller
                                            name={field.name}
                                            control={control}
                                            defaultValue=""
                                            render={(props) => controlRender(props, field)}
                                        />
                                    </Field>
                                );

                                return field.name === 'storageAddress' ? (
                                    <AddressField key={field.name}>{content}</AddressField>
                                ) : (
                                    content
                                );
                            })}
                        </FieldsGrid>
                    </form>
                    <EmployeesHeader>
                        <EmployeesTitle>{t('pages.profile.storages.employees')}</EmployeesTitle>
                        {hasAssignedEmployees && canManageEmployees && (
                            <InlineButton
                                type="button"
                                size="sm"
                                onClick={() => setAssignModalVisible(true)}
                            >
                                {t('pages.profile.storages.assignEmployees')} +
                            </InlineButton>
                        )}
                    </EmployeesHeader>
                    <EmployeesTableWrapper>
                        <Table
                            columns={columns}
                            data={mappedEmployees}
                            hideSetting
                            emptyRowsLimit={{rows: 4, translateY: '30%'}}
                            emptyStateComponent={emptyEmployeesState}
                        />
                    </EmployeesTableWrapper>
                </Container>
                <Modal
                    show={assignModalVisible}
                    onClose={() => {
                        setAssignModalVisible(false);
                        setSelectedEmployees([]);
                    }}
                    title={t('pages.profile.participant.add.title')}
                    modalWidth={0}
                >
                    <Select
                        menuPlacement="top"
                        isMulti
                        value={selectedEmployees}
                        options={availableEmployeeOptions}
                        placeholder={t('pages.profile.participant.fields.userIds')}
                        onChange={(selected: SelectOption<number>[]) =>
                            setSelectedEmployees(selected ?? [])
                        }
                    />
                    <ModalFooter>
                        <InlineButton
                            type="button"
                            size="sm"
                            variant={ButtonVariant.Secondary}
                            onClick={() => {
                                setAssignModalVisible(false);
                                setSelectedEmployees([]);
                            }}
                        >
                            {t('pages.profile.participant.add.cancel')}
                        </InlineButton>
                        <InlineButton
                            type="button"
                            size="sm"
                            disabled={selectedEmployees.length === 0}
                            onClick={onAssignEmployees}
                        >
                            {t('pages.profile.participant.add.ok')}
                        </InlineButton>
                    </ModalFooter>
                </Modal>
                <RemoveConfirmationModal
                    modalWidth={0}
                    show={!!employeeForRemoveConfirmation}
                    hideCloseButton
                    onClose={() => setEmployeeForRemoveConfirmation(null)}
                >
                    <ConfirmRemoveModalContent>
                        <ConfirmRemoveHeader>
                            <ConfirmRemoveTitle>
                                <ConfirmRemoveHeaderIcon>
                                    <Icon
                                        source={WarningIcon}
                                        strokeColor="#FF3030"
                                        fillColor="transparent"
                                        width={20}
                                        height={20}
                                    />
                                </ConfirmRemoveHeaderIcon>
                                {t('pages.profile.storages.removeEmployee.title')}
                            </ConfirmRemoveTitle>
                            <ModalCloseButton
                                type="button"
                                onClick={() => setEmployeeForRemoveConfirmation(null)}
                            >
                                <Icon
                                    source={CrossIcon}
                                    fillColor="#939AA4"
                                    width={12}
                                    height={12}
                                />
                            </ModalCloseButton>
                        </ConfirmRemoveHeader>
                        <WarningMessage>
                            <p>
                                {t('pages.profile.storages.removeEmployee.message', {
                                    name: employeeForRemoveConfirmation
                                        ? getFullName(employeeForRemoveConfirmation)
                                        : '',
                                })}
                            </p>
                        </WarningMessage>
                    </ConfirmRemoveModalContent>
                    <ConfirmRemoveFooter>
                        <CancelConfirmButton
                            size="sm"
                            variant={ButtonVariant.Outline}
                            onClick={() => setEmployeeForRemoveConfirmation(null)}
                        >
                            {t('pages.profile.staff.confirm.cancel')}
                        </CancelConfirmButton>
                        <ConfirmDeleteButton size="sm" onClick={onConfirmRemoveEmployee}>
                            {t('pages.profile.staff.confirm.delete')}
                        </ConfirmDeleteButton>
                    </ConfirmRemoveFooter>
                </RemoveConfirmationModal>
                <ConfirmBlockModal
                    onDeactivateStorage={onDeactivateStorage}
                    isBlockModalVisible={isBlockModalVisible}
                    setIsBlockModalVisible={setIsBlockModalVisible}
                    deactivatePending={deactivatePending}
                />
            </Wrapper>
        </Page>
    );
};
